import { useEffect, useState } from "react";
import { FiTag, FiX } from "react-icons/fi";

const emptyForm = {
  name: "",
  status: "DRAFT",
  showInPromoBar: false,
  promoBarText: "",
  ctaLabel: "",
  ctaUrl: "",
  sortOrder: 0,
  startsAt: "",
  endsAt: "",
  includeCoupon: true,
  couponCode: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  maxDiscountAmount: "",
  minimumOrderAmount: "",
  maxRedemptions: "",
  perCustomerLimit: 1,
  couponIsActive: true,
};

const toDateTimeLocal = (value) => {
  if (!value) return "";

  const date = new Date(value);

  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000)
    .toISOString()
    .slice(0, 16);
};

export default function PromotionModal({
  open,
  promotion,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    const coupon = promotion?.coupon;

    setError("");
    setForm({
      name: promotion?.name || "",
      status: promotion?.status || "DRAFT",
      showInPromoBar: promotion?.showInPromoBar || false,
      promoBarText: promotion?.promoBarText || "",
      ctaLabel: promotion?.ctaLabel || "",
      ctaUrl: promotion?.ctaUrl || "",
      sortOrder: promotion?.sortOrder || 0,
      startsAt: toDateTimeLocal(promotion?.startsAt),
      endsAt: toDateTimeLocal(promotion?.endsAt),
      includeCoupon: Boolean(coupon) || !promotion,
      couponCode: coupon?.code || "",
      discountType: coupon?.discountType || "PERCENTAGE",
      discountValue: coupon?.discountValue ?? "",
      maxDiscountAmount: coupon?.maxDiscountAmount ?? "",
      minimumOrderAmount: coupon?.minimumOrderAmount ?? "",
      maxRedemptions: coupon?.maxRedemptions ?? "",
      perCustomerLimit: coupon?.perCustomerLimit || 1,
      couponIsActive: coupon?.isActive ?? true,
    });
  }, [open, promotion]);

  if (!open) return null;

  const update = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.name.trim()) {
      setError("Campaign name is required.");
      return;
    }

    if (form.showInPromoBar && !form.promoBarText.trim()) {
      setError("Promo-bar text is required when visible on the website.");
      return;
    }

    if (form.includeCoupon) {
      if (!form.couponCode.trim() || !form.discountValue) {
        setError("Coupon code and discount value are required.");
        return;
      }
    }
    if (form.startsAt && form.endsAt && new Date(form.endsAt) < new Date(form.startsAt)) {
      setError('End date cannot be before start date.'); return;
    }
    if (form.includeCoupon && form.discountType === 'PERCENTAGE' && Number(form.discountValue) > 100) {
      setError('Percentage discount cannot exceed 100%.'); return;
    }

    const payload = {
      name: form.name.trim(),
      status: form.status,
      showInPromoBar: form.showInPromoBar,
      promoBarText: form.promoBarText.trim() || undefined,
      ctaLabel: form.ctaLabel.trim() || undefined,
      ctaUrl: form.ctaUrl.trim() || undefined,
      sortOrder: Number(form.sortOrder || 0),
      startsAt: form.startsAt
        ? new Date(form.startsAt).toISOString()
        : null,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      ...(form.includeCoupon
        ? {
            coupon: {
              code: form.couponCode.trim().toUpperCase(),
              discountType: form.discountType,
              discountValue: Number(form.discountValue),
              maxDiscountAmount: form.discountType === 'PERCENTAGE' && form.maxDiscountAmount !== '' ? Number(form.maxDiscountAmount) : null,
              minimumOrderAmount: Number(form.minimumOrderAmount || 0),
              maxRedemptions: form.maxRedemptions !== '' ? Number(form.maxRedemptions) : null,
              perCustomerLimit: Number(form.perCustomerLimit || 1),
              isActive: form.couponIsActive,
              startsAt: form.startsAt
                ? new Date(form.startsAt).toISOString()
                : undefined,
              endsAt: form.endsAt
                ? new Date(form.endsAt).toISOString()
                : undefined,
            },
          }
        : promotion?.coupon ? { coupon: { code: promotion.coupon.code, discountType: promotion.coupon.discountType, discountValue: Number(promotion.coupon.discountValue), maxDiscountAmount: promotion.coupon.maxDiscountAmount == null ? null : Number(promotion.coupon.maxDiscountAmount), minimumOrderAmount: Number(promotion.coupon.minimumOrderAmount), maxRedemptions: promotion.coupon.maxRedemptions, perCustomerLimit: promotion.coupon.perCustomerLimit, isActive: false } } : {}),
    };

    try {
      setSaving(true);
      await onSave(payload);
    } catch (requestError) {
      setError(requestError.message || "Unable to save promotion.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {promotion ? "Edit Promotion" : "Create Promotion"}
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Configure a coupon and optional storefront promo bar.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[70vh] space-y-7 overflow-y-auto p-8">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <section>
              <h3 className="mb-4 text-lg font-bold text-gray-900">
                Campaign Details
              </h3>

              <div className="grid gap-5 md:grid-cols-2">
                <Input
                  label="Campaign Name *"
                  value={form.name}
                  onChange={(event) => update("name", event.target.value)}
                  placeholder="Summer Welcome Offer"
                />

                <Select
                  label="Status"
                  value={form.status}
                  onChange={(event) => update("status", event.target.value)}
                  options={["DRAFT", "ACTIVE", "INACTIVE"]}
                />

                <Input
                  label="Starts At"
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(event) => update("startsAt", event.target.value)}
                />

                <Input
                  label="Ends At"
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(event) => update("endsAt", event.target.value)}
                />

                <Input
                  label="Priority"
                  type="number"
                  min="0"
                  value={form.sortOrder}
                  onChange={(event) => update("sortOrder", event.target.value)}
                  placeholder="0"
                />
              </div>
            </section>

            <section className="border-t border-gray-100 pt-7">
              <Toggle
                checked={form.showInPromoBar}
                onChange={(checked) => update("showInPromoBar", checked)}
                label="Show in storefront promo bar"
                description="Only active, currently scheduled campaigns appear publicly."
              />

              {form.showInPromoBar && (
                <div className="mt-5 grid gap-5 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <Input
                      label="Promo-bar Text *"
                      value={form.promoBarText}
                      onChange={(event) =>
                        update("promoBarText", event.target.value)
                      }
                      placeholder="Use code SUMMER10 and save 10% on your order"
                    />
                  </div>

                  <Input
                    label="CTA Label"
                    value={form.ctaLabel}
                    onChange={(event) => update("ctaLabel", event.target.value)}
                    placeholder="Shop Now"
                  />

                  <Input
                    label="CTA Link"
                    value={form.ctaUrl}
                    onChange={(event) => update("ctaUrl", event.target.value)}
                    placeholder="/search/all"
                  />
                </div>
              )}
            </section>

            <section className="border-t border-gray-100 pt-7">
              <Toggle
                checked={form.includeCoupon}
                onChange={(checked) => update("includeCoupon", checked)}
                label="Attach a coupon code"
                description="Valid in POS and online checkout. Select a customer in POS. Usage limits are shared across both channels. Turning this off disables the existing coupon."
              />

              {form.includeCoupon && (
                <div className="mt-5 grid gap-5 md:grid-cols-2">
                  <Input
                    label="Coupon Code *"
                    value={form.couponCode}
                    onChange={(event) =>
                      update("couponCode", event.target.value.toUpperCase())
                    }
                    placeholder="SUMMER10"
                  />

                  <Select
                    label="Discount Type"
                    value={form.discountType}
                    onChange={(event) =>
                      update("discountType", event.target.value)
                    }
                    options={["PERCENTAGE", "FIXED_AMOUNT"]}
                  />

                  <Input
                    label={
                      form.discountType === "PERCENTAGE"
                        ? "Discount Percentage *"
                        : "Discount Amount (₹) *"
                    }
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.discountValue}
                    onChange={(event) =>
                      update("discountValue", event.target.value)
                    }
                    placeholder="10"
                  />

                  {form.discountType === "PERCENTAGE" ? (
                    <Input
                      label="Maximum Discount (₹)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.maxDiscountAmount}
                      onChange={(event) =>
                        update("maxDiscountAmount", event.target.value)
                      }
                      placeholder="500"
                    />
                  ) : (
                    <Input
                      label="Minimum Order Value (₹)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.minimumOrderAmount}
                      onChange={(event) =>
                        update("minimumOrderAmount", event.target.value)
                      }
                      placeholder="1000"
                    />
                  )}

                  {form.discountType === "PERCENTAGE" && (
                    <Input
                      label="Minimum Order Value (₹)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.minimumOrderAmount}
                      onChange={(event) =>
                        update("minimumOrderAmount", event.target.value)
                      }
                      placeholder="1000"
                    />
                  )}

                  <Input
                    label="Maximum Total Redemptions"
                    type="number"
                    min="1"
                    value={form.maxRedemptions}
                    onChange={(event) =>
                      update("maxRedemptions", event.target.value)
                    }
                    placeholder="Leave empty for unlimited"
                  />

                  <Input
                    label="Uses Per Customer"
                    type="number"
                    min="1"
                    value={form.perCustomerLimit}
                    onChange={(event) =>
                      update("perCustomerLimit", event.target.value)
                    }
                    placeholder="1"
                  />

                  <div className="md:col-span-2">
                    <Toggle
                      checked={form.couponIsActive}
                      onChange={(checked) => update("couponIsActive", checked)}
                      label="Coupon is active"
                      description="Requires an ACTIVE campaign and valid dates. Inactive coupons cannot be applied in POS or online."
                    />
                  </div>
                </div>
              )}
            </section>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-11 rounded-xl border border-gray-200 px-6 font-medium text-gray-700"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-6 font-semibold text-white hover:bg-primary-800 disabled:opacity-60"
            >
              <FiTag />
              {saving
                ? "Saving..."
                : promotion
                  ? "Save Changes"
                  : "Create Promotion"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-gray-700">
        {label}
      </span>

      <input
        {...props}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
      />
    </label>
  );
}

function Select({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-gray-700">
        {label}
      </span>

      <select
        {...props}
        className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option.replaceAll("_", " ")}
          </option>
        ))}
      </select>
    </label>
  );
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-gray-100 p-4">
      <div>
        <p className="font-semibold text-gray-900">{label}</p>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>

      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-5 w-5 accent-primary-800"
      />
    </label>
  );
}
